import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import BlogProgressBar from '@/components/feature/BlogProgressBar';
import BlogRecommendations from '@/components/feature/BlogRecommendations';
import BlogTableOfContents from '@/components/feature/BlogTableOfContents';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

const TOP_10_ITEMS = [
  {
    num: 1,
    phase: 'Phase 1 — Assess Your Needs',
    phaseColor: 'bg-[#1A3A52]',
    title: 'Calculate your total coverage need using the DIME method',
    summary: 'Add up four numbers: Debt (mortgage + loans + credit cards + final expenses), Income replacement (annual income × years needed), Mortgage balance, and Education costs per child. This gives you your total coverage target.',
    tip: 'Indiana average: families need 10–12× annual income. A $75K earner with 2 kids typically needs $1.2M–$1.5M in coverage.',
    link: { to: '/resources/indiana-life-insurance-calculator', label: 'Use the free Indiana coverage calculator' },
    icon: 'ri-calculator-line',
  },
  {
    num: 2,
    phase: 'Phase 1 — Assess Your Needs',
    phaseColor: 'bg-[#1A3A52]',
    title: 'Identify your coverage gap',
    summary: 'Subtract your existing coverage (employer group life + individual policies) and liquid savings from your total coverage need. This gap is the exact amount of new insurance you need to buy. Most Indiana families are shocked by how large this number is.',
    tip: 'Most Indiana employers offer only 1–2× salary — and it disappears if you change jobs.',
    icon: 'ri-bar-chart-grouped-line',
  },
  {
    num: 3,
    phase: 'Phase 2 — Gather Your Information',
    phaseColor: 'bg-[#D97642]',
    title: 'List all prescription medications (past 5–10 years)',
    summary: 'Underwriters check the MIB (Medical Information Bureau) and prescription drug databases. Be thorough — omissions can be flagged as misrepresentation and used to deny a claim during the 2-year contestability period.',
    tip: 'Include dosage and the condition being treated. Controlled medications require extra documentation.',
    icon: 'ri-medicine-bottle-line',
  },
  {
    num: 4,
    phase: 'Phase 2 — Gather Your Information',
    phaseColor: 'bg-[#D97642]',
    title: 'Know your tobacco and nicotine use history',
    summary: 'Carriers ask about cigarettes, cigars, chewing tobacco, vaping, nicotine patches, and marijuana. Most Indiana carriers require 12 months nicotine-free to qualify for non-tobacco rates. Quitting before applying can cut your premium by 50–60%.',
    tip: 'Indiana carriers test for cotinine (nicotine metabolite) in urine during the paramedical exam.',
    icon: 'ri-forbid-line',
  },
  {
    num: 5,
    phase: 'Phase 3 — Shop & Compare Carriers',
    phaseColor: 'bg-teal-600',
    title: 'Work with an independent agent (not a captive agent)',
    summary: 'An independent agent shops your case across 20+ carriers simultaneously. A captive agent (State Farm, Northwestern Mutual) can only offer their company\'s products. For the same health profile, rates can vary 30–50% between carriers — independent agents almost always find better rates.',
    tip: 'Hoosier Life Insurance is an independent agency — we shop all major Indiana carriers at no extra cost to you.',
    link: { to: '/book-appointment', label: 'Talk to an independent Indiana agent' },
    icon: 'ri-store-2-line',
  },
  {
    num: 6,
    phase: 'Phase 3 — Shop & Compare Carriers',
    phaseColor: 'bg-teal-600',
    title: 'Verify each carrier\'s AM Best financial strength rating',
    summary: 'Only buy from carriers rated A- or better by AM Best. This ensures the company will be financially able to pay your claim decades from now. Top Indiana carriers: Banner Life (A+), Protective (A+), Mutual of Omaha (A+), Nationwide (A+).',
    link: { to: '/resources/how-to-choose-life-insurance-company-indiana', label: 'Full carrier selection guide' },
    icon: 'ri-shield-star-line',
  },
  {
    num: 7,
    phase: 'Phase 4 — Complete the Application',
    phaseColor: 'bg-amber-600',
    title: 'Answer all application questions completely and honestly',
    summary: 'Material misrepresentation — false or incomplete answers — can result in policy cancellation or claim denial during the 2-year contestability period. Indiana follows the standard 2-year rule. After 2 years, the policy is generally incontestable. When in doubt, disclose.',
    tip: 'Your agent can help you frame health conditions accurately without over-disclosing.',
    icon: 'ri-file-edit-line',
  },
  {
    num: 8,
    phase: 'Phase 4 — Complete the Application',
    phaseColor: 'bg-amber-600',
    title: 'Name your primary and contingent beneficiaries',
    summary: 'Primary beneficiary: who gets the death benefit first (usually your spouse). Contingent beneficiary: backup if the primary predeceases you (usually children or a trust). Always name both. An outdated or missing beneficiary designation can send your death benefit into probate.',
    tip: 'If your children are minors, consider naming a trust as contingent beneficiary so a trustee manages the funds until they reach adulthood.',
    icon: 'ri-user-heart-line',
  },
  {
    num: 9,
    phase: 'Phase 5 — Review & Accept Your Policy',
    phaseColor: 'bg-emerald-600',
    title: 'Read the policy during your free look period',
    summary: 'Indiana law requires a minimum 10-day free look period. Read the entire policy — especially the exclusions, riders, and beneficiary designations. You can cancel for a full refund during this period, no questions asked.',
    tip: 'If you received a table rating, ask your agent to shop the case to other carriers — ratings vary significantly between companies.',
    icon: 'ri-shield-check-line',
  },
  {
    num: 10,
    phase: 'Phase 6 — After Your Policy Is Active',
    phaseColor: 'bg-rose-600',
    title: 'Update beneficiaries after every major life event',
    summary: 'Marriage, divorce, birth of a child, or death of a beneficiary all require a beneficiary update. Indiana courts have ruled that a named ex-spouse can still receive the death benefit if the beneficiary designation was never updated after divorce. Review your policy every 2–3 years.',
    link: { to: '/book-appointment', label: 'Schedule a free coverage review' },
    icon: 'ri-calendar-check-line',
  },
];

export default function LifeInsuranceChecklistIndianaBlog() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Indiana Life Insurance Checklist: 10 Steps Before You Buy | Hoosier Life';

    const setMeta = (nameOrProp: string, content: string) => {
      let el = document.querySelector(`meta[name="${nameOrProp}"]`) || document.querySelector(`meta[property="${nameOrProp}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (nameOrProp.startsWith('og:') || nameOrProp.startsWith('twitter:')) {
          el.setAttribute('property', nameOrProp);
        } else {
          el.setAttribute('name', nameOrProp);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'The top 10 steps every Indiana family must complete before buying life insurance — from calculating your coverage gap to naming beneficiaries. Plus: the full 35-step interactive checklist.');
    setMeta('keywords', 'life insurance checklist Indiana, buying life insurance Indiana, life insurance steps Indiana, Indiana life insurance guide, Indianapolis life insurance checklist');
    setMeta('last-modified', new Date().toISOString());
    setMeta('og:title', 'Indiana Life Insurance Checklist: Top 10 Steps Before You Buy');
    setMeta('og:description', 'The top 10 steps every Indiana family must complete before buying life insurance — from calculating your coverage gap to naming beneficiaries.');
    setMeta('og:url', `${SITE_URL}/blog/life-insurance-checklist-indiana`);
    setMeta('og:type', 'article');
    setMeta('og:image', 'https://public.readdy.ai/ai/img_res/07ef059fb7df3659469553ea8335bb2c.jpg');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:image', 'https://readdy.ai/api/search-image?query=Indiana%20family%20sitting%20at%20kitchen%20table%20reviewing%20life%20insurance%20checklist%20documents%20with%20pen%20and%20coffee%20warm%20natural%20light%20organized%20planning%20session%20lifestyle%20photography%20clean%20bright%20home&width=1200&height=630&seq=og-checklist-blog&orientation=landscape');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/blog/life-insurance-checklist-indiana`);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Indiana Life Insurance Checklist: Top 10 Steps Before You Buy',
        description: 'The top 10 steps every Indiana family must complete before buying life insurance — from calculating your coverage gap to naming beneficiaries.',
        author: {
          '@type': 'Person',
          '@id': `${SITE_URL}/about#agent`,
          name: 'Benjamin Wright',
          jobTitle: 'Licensed Life Insurance Agent',
          url: `${SITE_URL}/about`,
          worksFor: { '@type': 'InsuranceAgency', name: 'Hoosier Life Insurance', url: SITE_URL },
        },
        publisher: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL },
        datePublished: '2025-04-12',
        dateModified: '2025-04-12T00:00:00Z',
        url: `${SITE_URL}/blog/life-insurance-checklist-indiana`,
        mainEntityOfPage: `${SITE_URL}/blog/life-insurance-checklist-indiana`,
        image: {
          '@type': 'ImageObject',
          url: 'https://readdy.ai/api/search-image?query=Indiana%20family%20sitting%20at%20kitchen%20table%20reviewing%20life%20insurance%20checklist%20documents%20with%20pen%20and%20coffee%20warm%20natural%20light%20organized%20planning%20session%20lifestyle%20photography%20clean%20bright%20home&width=1200&height=630&seq=og-checklist-blog&orientation=landscape',
          width: 1200,
          height: 630,
        },
        timeRequired: 'PT8M',
        wordCount: 1800,
        inLanguage: 'en-US',
        isPartOf: { '@type': 'WebSite', url: SITE_URL },
        about: { '@type': 'Thing', name: 'Life Insurance Checklist Indiana' },
        mentions: [
          { '@type': 'WebPage', name: 'Indiana Life Insurance Buying Checklist (Full Interactive Tool)', url: `${SITE_URL}/resources/life-insurance-checklist-indiana` },
          { '@type': 'WebPage', name: 'Indiana Life Insurance Calculator', url: `${SITE_URL}/resources/indiana-life-insurance-calculator` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
          { '@type': 'ListItem', position: 3, name: 'Indiana Life Insurance Checklist', item: `${SITE_URL}/blog/life-insurance-checklist-indiana` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What should I do before buying life insurance in Indiana?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Before buying life insurance in Indiana, you should: (1) calculate your total coverage need using the DIME method, (2) identify your coverage gap by subtracting existing coverage from your total need, (3) gather your medical history and prescription list, (4) work with an independent agent to shop multiple carriers, and (5) verify each carrier\'s AM Best financial strength rating. Our full 35-step checklist walks you through every phase from assessment to policy management.',
            },
          },
          {
            '@type': 'Question',
            name: 'How much life insurance do Indiana families need?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Most Indiana families need 10–12× their annual income plus outstanding debts. A $75,000/year earner with a mortgage, two children, and typical debts typically needs $1.2M–$1.5M in coverage. Use our free Indiana life insurance calculator to get your exact number based on your specific situation.',
            },
          },
          {
            '@type': 'Question',
            name: 'What is the free look period for life insurance in Indiana?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Indiana law requires a minimum 10-day free look period for all life insurance policies. During this period, you can review the policy and cancel for a full refund if you are not satisfied — no questions asked. Use this time to verify all policy details, beneficiary designations, and riders.',
            },
          },
        ],
      },
    ];

    schemas.forEach((schema, i) => {
      let script = document.getElementById(`schema-blog-checklist-${i}`) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = `schema-blog-checklist-${i}`;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    });

    return () => {
      schemas.forEach((_, i) => document.getElementById(`schema-blog-checklist-${i}`)?.remove());
    };
  }, []);

  return (
    <>
      <title>Indiana Life Insurance Checklist: 10 Steps Before You Buy | Hoosier Life</title>
      <meta name="description" content="The top 10 steps every Indiana family must complete before buying life insurance — from calculating your coverage gap to naming beneficiaries. Plus: the full 35-step interactive checklist." />
      <link rel="canonical" href={`${SITE_URL}/blog/life-insurance-checklist-indiana`} />
      <meta name="robots" content="index, follow" />

      <div className="min-h-screen bg-white">
        <BlogProgressBar />
        <Header />

        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="bg-[#1A3A52] border-b border-[#1A3A52]/20">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
            <Link to="/" className="flex items-center gap-1.5 text-white/70 hover:text-[#D97642] transition-colors">
              <i className="ri-home-4-line text-base" />
              <span>Home</span>
            </Link>
            <i className="ri-arrow-right-s-line text-white/40" />
            <Link to="/blog" className="text-white/70 hover:text-[#D97642] transition-colors">Blog</Link>
            <i className="ri-arrow-right-s-line text-white/40" />
            <span className="text-[#D97642] font-semibold">Indiana Life Insurance Checklist</span>
          </div>
        </nav>

        <article className="max-w-4xl mx-auto px-6 py-16">

          {/* Hero */}
          <header className="mb-12">
            <div className="flex flex-wrap gap-3 mb-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#D97642]/10 text-[#D97642] text-xs font-bold rounded-full border border-[#D97642]/20">
                <i className="ri-checkbox-multiple-line" />Checklist Guide
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">
                <i className="ri-map-pin-line" />Indiana
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Indiana Life Insurance Checklist: 10 Steps Every Hoosier Must Complete Before Buying
            </h1>
            <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-8">
              <span className="flex items-center gap-2"><i className="ri-calendar-line text-[#D97642]" />April 2025</span>
              <span className="flex items-center gap-2"><i className="ri-time-line text-[#D97642]" />8 min read</span>
              <span className="flex items-center gap-2"><i className="ri-user-line text-[#D97642]" />Benjamin Wright, Licensed Indiana Agent</span>
            </div>
            <p className="text-xl text-gray-700 leading-relaxed">
              Buying life insurance in Indiana doesn't have to be overwhelming. These are the 10 most critical steps — pulled from our full 35-step interactive checklist — that every Indiana family should complete before signing anything.
            </p>
          </header>

          {/* Hero Image */}
          <div className="relative rounded-2xl overflow-hidden mb-12 h-72 md:h-96">
            <img
              src="https://readdy.ai/api/search-image?query=Indiana%20family%20sitting%20at%20kitchen%20table%20reviewing%20life%20insurance%20checklist%20documents%20with%20pen%20and%20coffee%20warm%20natural%20light%20organized%20planning%20session%20lifestyle%20photography%20clean%20bright%20home%20interior&width=1200&height=600&seq=blog-checklist-hero&orientation=landscape"
              alt="Indiana family reviewing life insurance checklist"
              className="w-full h-full object-cover object-top"
              width={1200}
              height={600}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            <div className="absolute bottom-5 left-6 right-6">
              <p className="text-white/80 text-sm">Most Indiana families skip 3–4 of these steps — and pay for it later.</p>
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="bg-[#1A3A52] text-white rounded-2xl p-7 mb-12">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <i className="ri-bookmark-3-fill text-[#D97642] text-xl" />
              Key Takeaways
            </h2>
            <ul className="space-y-2.5">
              {[
                'Most Indiana families need 10–12× their annual income in coverage — most are significantly underinsured',
                'Tobacco use can double or triple your premium — quitting 12 months before applying saves 50–60%',
                'Working with an independent agent (not a captive agent) can save 30–50% on the same coverage',
                'Indiana\'s 10-day free look period lets you cancel any policy for a full refund — use it',
                'Outdated beneficiary designations are one of the most common and costly mistakes Indiana families make',
              ].map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                  <i className="ri-check-line text-[#D97642] mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* Table of Contents */}
          <BlogTableOfContents
            title="In This Guide"
            items={[
              { id: 'intro', label: 'Why a Checklist Matters' },
              { id: 'top-10', label: 'The Top 10 Checklist Items' },
              { id: 'full-checklist', label: 'The Full 35-Step Interactive Checklist' },
              { id: 'faq', label: 'Frequently Asked Questions' },
            ]}
          />

          {/* Intro Section */}
          <section id="intro" className="mt-12 mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why a Checklist Matters When Buying Life Insurance in Indiana</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Life insurance applications are legally binding documents. Missing a step — like disclosing a medication or naming a contingent beneficiary — can result in a denied claim or a policy that doesn't actually protect your family. A checklist keeps you from making expensive, irreversible mistakes.
            </div>
            <p className="text-gray-700 leading-relaxed mb-5">
              Indiana families buy life insurance during some of the most stressful moments of their lives — after a new baby, a home purchase, or a health scare. In that state of mind, it's easy to rush through the process and miss critical steps.
            </p>
            <p className="text-gray-700 leading-relaxed mb-5">
              We built our <Link to="/resources/life-insurance-checklist-indiana" className="text-[#D97642] font-semibold hover:underline">full 35-step interactive checklist</Link> after seeing the same mistakes repeated by Indiana families year after year. This blog post summarizes the 10 most important items — the ones that have the biggest impact on your coverage, your premium, and your family's financial security.
            </p>

            {/* Inline CTA */}
            <div className="bg-gradient-to-br from-[#FDF6F0] to-[#FEF3EC] border border-[#D97642]/30 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-12 h-12 bg-[#D97642] text-white rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-checkbox-multiple-line text-xl" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-900 text-sm mb-0.5">Want the full 35-step checklist?</p>
                <p className="text-gray-600 text-xs">Interactive, trackable, and printable — free for Indiana residents.</p>
              </div>
              <Link
                to="/resources/life-insurance-checklist-indiana"
                className="inline-flex items-center gap-2 bg-[#D97642] text-white px-5 py-2.5 rounded-lg font-bold text-sm hover:bg-[#C4663A] transition-colors whitespace-nowrap"
              >
                <i className="ri-external-link-line" />Open Full Checklist
              </Link>
            </div>
          </section>

          {/* Top 10 Items */}
          <section id="top-10" className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">The Top 10 Checklist Items</h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              These 10 items are drawn from all 6 phases of the full checklist. They're the steps that most commonly get skipped — and the ones with the highest consequences when they are.
            </p>

            <div className="space-y-6">
              {TOP_10_ITEMS.map((item) => (
                <div key={item.num} className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-[#D97642]/40 transition-colors">
                  <div className="flex items-start gap-0">
                    {/* Number Badge */}
                    <div className={`${item.phaseColor} text-white w-14 flex-shrink-0 flex flex-col items-center justify-center py-6 self-stretch`}>
                      <span className="text-2xl font-bold leading-none">{item.num}</span>
                    </div>

                    <div className="flex-1 p-6">
                      {/* Phase Label */}
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{item.phase}</p>

                      {/* Title */}
                      <h3 className="text-lg font-bold text-gray-900 mb-3 leading-snug flex items-start gap-2">
                        <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className={`${item.icon} text-[#D97642]`} />
                        </span>
                        {item.title}
                      </h3>

                      {/* Summary */}
                      <p className="text-gray-700 text-sm leading-relaxed mb-4">{item.summary}</p>

                      {/* Tip */}
                      {item.tip && (
                        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mb-4">
                          <i className="ri-lightbulb-line text-amber-500 text-sm flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-800 leading-relaxed">{item.tip}</p>
                        </div>
                      )}

                      {/* Link */}
                      {item.link && (
                        <Link
                          to={item.link.to}
                          className="inline-flex items-center gap-1.5 text-sm text-[#D97642] font-semibold hover:underline"
                        >
                          <i className="ri-arrow-right-circle-line" />
                          {item.link.label}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Full Checklist CTA — Big Feature Block */}
          <section id="full-checklist" className="mb-12">
            <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 md:p-10">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-[#D97642]/20 text-[#D97642] border border-[#D97642]/30 rounded-full px-4 py-1.5 text-sm font-semibold mb-5">
                    <i className="ri-checkbox-multiple-line" />Free Interactive Tool
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-4 leading-tight">
                    The Full 35-Step Indiana Life Insurance Checklist
                  </h2>
                  <p className="text-white/75 leading-relaxed mb-6">
                    These 10 items are just the highlights. The full interactive checklist covers all 6 phases — 35 steps total — with Indiana-specific tips, pro warnings, and cross-links to related guides on every item.
                  </p>
                  <ul className="space-y-2.5 mb-7">
                    {[
                      { icon: 'ri-checkbox-circle-line', text: 'Check off each step as you complete it' },
                      { icon: 'ri-bar-chart-line', text: 'Live progress bar tracks your completion' },
                      { icon: 'ri-printer-line', text: 'Print the full checklist as a PDF' },
                      { icon: 'ri-lightbulb-line', text: 'Indiana-specific tips on every step' },
                    ].map(f => (
                      <li key={f.text} className="flex items-center gap-3 text-sm text-white/80">
                        <i className={`${f.icon} text-teal-400 flex-shrink-0`} />
                        {f.text}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/resources/life-insurance-checklist-indiana"
                    className="inline-flex items-center gap-2 bg-[#D97642] text-white px-7 py-3.5 rounded-xl font-bold hover:bg-[#C4663A] transition-colors whitespace-nowrap"
                  >
                    <i className="ri-external-link-line" />Open the Full Checklist
                  </Link>
                </div>

                {/* Phase Preview */}
                <div className="space-y-3">
                  {[
                    { phase: 'Phase 1', title: 'Assess Your Needs', steps: 5, color: 'bg-[#1A3A52] border-[#1A3A52]/40', icon: 'ri-search-eye-line' },
                    { phase: 'Phase 2', title: 'Gather Your Information', steps: 8, color: 'bg-[#D97642] border-[#D97642]/40', icon: 'ri-folder-open-line' },
                    { phase: 'Phase 3', title: 'Shop & Compare Carriers', steps: 6, color: 'bg-teal-600 border-teal-600/40', icon: 'ri-store-2-line' },
                    { phase: 'Phase 4', title: 'Complete the Application', steps: 6, color: 'bg-amber-600 border-amber-600/40', icon: 'ri-file-edit-line' },
                    { phase: 'Phase 5', title: 'Review & Accept Your Policy', steps: 5, color: 'bg-emerald-600 border-emerald-600/40', icon: 'ri-shield-check-line' },
                    { phase: 'Phase 6', title: 'After Your Policy Is Active', steps: 6, color: 'bg-rose-600 border-rose-600/40', icon: 'ri-calendar-check-line' },
                  ].map(p => (
                    <div key={p.phase} className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-3">
                      <div className={`w-8 h-8 ${p.color} text-white rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <i className={`${p.icon} text-sm`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{p.title}</p>
                        <p className="text-white/50 text-xs">{p.phase} · {p.steps} steps</p>
                      </div>
                      <span className="text-white/40 text-xs font-bold">{p.steps}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3 border border-white/10">
                    <span className="text-white/60 text-sm">Total steps</span>
                    <span className="text-white font-bold">35</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Related Tools Strip */}
          <section className="mb-12">
            <h2 className="text-xl font-bold text-gray-900 mb-5">Related Tools & Guides</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {
                  to: '/resources/indiana-life-insurance-calculator',
                  icon: 'ri-calculator-line',
                  title: 'Indiana Coverage Calculator',
                  desc: 'Find your exact coverage gap in 5 questions',
                  tag: 'Free Tool',
                },
                {
                  to: '/resources/how-to-choose-life-insurance-company-indiana',
                  icon: 'ri-shield-star-line',
                  title: 'How to Choose a Carrier in Indiana',
                  desc: '6 criteria and 7 questions before you buy',
                  tag: 'Guide',
                },
                {
                  to: '/resources/life-insurance-glossary-indiana',
                  icon: 'ri-book-open-line',
                  title: 'Life Insurance Glossary',
                  desc: '55+ terms defined in plain English',
                  tag: 'Reference',
                },
                {
                  to: '/resources/life-insurance-mistakes-indiana-families',
                  icon: 'ri-error-warning-line',
                  title: '5 Mistakes Indiana Families Make',
                  desc: 'Common errors and how to avoid them',
                  tag: 'Guide',
                },
              ].map(tool => (
                <Link
                  key={tool.to}
                  to={tool.to}
                  className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-5 hover:border-[#D97642]/40 hover:shadow-md transition-all group"
                >
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className={`${tool.icon} text-lg`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-bold text-gray-900 text-sm group-hover:text-[#D97642] transition-colors">{tool.title}</p>
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full whitespace-nowrap">{tool.tag}</span>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">{tool.desc}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-[#D97642] transition-colors flex-shrink-0 mt-1" />
                </Link>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-5">
              {[
                {
                  q: 'What should I do before buying life insurance in Indiana?',
                  a: 'Before buying life insurance in Indiana, you should: (1) calculate your total coverage need using the DIME method, (2) identify your coverage gap by subtracting existing coverage from your total need, (3) gather your medical history and prescription list, (4) work with an independent agent to shop multiple carriers, and (5) verify each carrier\'s AM Best financial strength rating. Our full 35-step checklist walks you through every phase from assessment to policy management.',
                },
                {
                  q: 'How much life insurance do Indiana families need?',
                  a: 'Most Indiana families need 10–12× their annual income plus outstanding debts. A $75,000/year earner with a mortgage, two children, and typical debts typically needs $1.2M–$1.5M in coverage. Use our free Indiana life insurance calculator to get your exact number based on your specific situation.',
                },
                {
                  q: 'What is the free look period for life insurance in Indiana?',
                  a: 'Indiana law requires a minimum 10-day free look period for all life insurance policies. During this period, you can review the policy and cancel for a full refund if you are not satisfied — no questions asked. Use this time to verify all policy details, beneficiary designations, and riders.',
                },
                {
                  q: 'Can I get life insurance in Indiana without a medical exam?',
                  a: 'Yes. Most Indiana residents under age 60 qualify for no-exam life insurance up to $500,000–$1,000,000 through accelerated underwriting. Carriers use prescription databases and medical records to approve applications within 24–72 hours with no blood draw required. See our no-exam life insurance guide for details.',
                },
              ].map((faq, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-base font-bold text-gray-900 mb-3 flex items-start gap-2">
                    <span className="w-6 h-6 bg-[#D97642]/10 text-[#D97642] rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">Q</span>
                    {faq.q}
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed pl-8">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Author Bio */}
          <div className="border border-gray-200 rounded-2xl p-7 mb-10 flex items-start gap-5">
            <div className="w-14 h-14 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0">
              <i className="ri-shield-user-line text-white text-2xl" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-base mb-1">Written by Benjamin Wright — Licensed Indiana Life Insurance Agent</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">
                Benjamin is an independent life insurance agent serving Indianapolis, Carmel, Fishers, Noblesville, and all of Indiana. He has helped hundreds of Indiana families find the right coverage at the right price by shopping 20+ A-rated carriers. All content is reviewed for accuracy and updated regularly to reflect current Indiana market conditions.
              </p>
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]" />Licensed in Indiana</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]" />Independent — 20+ Carriers</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]" />Last reviewed April 2025</span>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 mb-4">
            <h2 className="text-2xl font-bold mb-3">Ready to Work Through the Full Checklist?</h2>
            <p className="text-white/75 mb-6 leading-relaxed">
              The full 35-step interactive checklist is free for Indiana residents. Check off each step as you go, track your progress, and print the completed checklist for your records.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/resources/life-insurance-checklist-indiana"
                className="inline-flex items-center justify-center gap-2 bg-[#D97642] text-white px-7 py-3.5 rounded-xl font-bold hover:bg-[#C4663A] transition-colors whitespace-nowrap"
              >
                <i className="ri-checkbox-multiple-line" />Open Full 35-Step Checklist
              </Link>
              <Link
                to="/book-appointment"
                className="inline-flex items-center justify-center gap-2 bg-white/10 text-white px-7 py-3.5 rounded-xl font-bold hover:bg-white/20 transition-colors border border-white/20 whitespace-nowrap"
              >
                <i className="ri-calendar-line" />Book a Free Consultation
              </Link>
            </div>
          </div>

        </article>

        {/* Quiz CTA Strip */}
        <section className="py-12 bg-gradient-to-br from-orange-50 to-amber-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
              <i className="ri-question-line text-3xl text-orange-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Not Sure Which Policy Is Right for You?</h3>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              Take our 2-minute quiz to get a personalized recommendation based on your family's unique needs and budget.
            </p>
            <Link
              to="/quiz"
              className="inline-flex items-center gap-2 bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap"
            >
              Take the Quiz Now
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>

        <BlogRecommendations topic="checklist" />

        <Footer />
      </div>
    </>
  );
}
